import { useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MapComponent from "./MapCompnent";
import { Marker, GeoJSON, useMap, Popup } from "react-leaflet";
import L from "leaflet";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import DOMPurify from "dompurify";


const EditableArea = ({ geoJsonData, color, onAreaUpdate }) => {
  const map = useMap();
  const layersRef = useRef([]); 

  useEffect(() => {
    if (!map) return;

    // Dodaj kontrolki Geoman do mapy
    map.pm.addControls({
      position: 'topright',
      drawCircle: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawMarker: false,
      drawText: false,
      cutPolygon: false,
    });

    // Zmień tekst tooltipów na polski
    map.pm.setLang('pl', {
      tooltips: {
        placeMarker: 'Kliknij aby umieścić marker',
        firstVertex: 'Kliknij aby rozpocząć rysowanie',
        continueLine: 'Kliknij aby kontynuować',
        finishLine: 'Kliknij na ostatni punkt aby zakończyć',
        finishPoly: 'Kliknij na pierwszy punkt aby zakończyć',
        finishRect: 'Kliknij aby zakończyć',
        startCircle: 'Kliknij aby rozpocząć rysowanie okręgu',
        finishCircle: 'Kliknij aby zakończyć okrąg',
        placeCircleMarker: 'Kliknij aby umieścić marker',
      },
      actions: {
        finish: 'Zakończ',
        cancel: 'Anuluj',
        removeLastVertex: 'Usuń ostatni punkt',
      },
      buttonTitles: {
        drawPolyButton: 'Narysuj wielokąt',
        drawLineButton: 'Narysuj linię',
        drawCircleButton: 'Narysuj okrąg',
        drawRectButton: 'Narysuj prostokąt',
        editButton: 'Edytuj warstwy',
        dragButton: 'Przeciągnij warstwy',
        cutButton: 'Wytnij warstwy',
        deleteButton: 'Usuń warstwy',
        drawMarkerButton: 'Narysuj marker',
      },
    });

    // Obsługa utworzenia nowego obszaru
    map.on('pm:create', (e) => {
      const layer = e.layer;
      const geoJson = layer.toGeoJSON();
      
      // Ustaw styl
      if (layer.setStyle) {
        layer.setStyle({
          color: color || '#ff0000',
          weight: 3,
          fillColor: color || '#ff0000',
          fillOpacity: 0.3,
        });
      }

      layer.pm.enable();

      // Dodaj do tablicy layerów (nie usuwaj starych)
      layersRef.current.push(layer);

      // Wyślij GeoJSON do parent - zbierz wszystkie layery
      const allLayers = layersRef.current.map(l => l.toGeoJSON());
      if (onAreaUpdate) {
        if (allLayers.length === 1) {
          onAreaUpdate(JSON.stringify(allLayers[0].geometry));
        } else {
          // Jeśli więcej niż jeden layer, wyślij FeatureCollection
          const featureCollection = {
            type: "FeatureCollection",
            features: allLayers
          };
          onAreaUpdate(JSON.stringify(featureCollection));
        }
      }

      // Obsługa edycji kształtu
      layer.on('pm:edit', () => {
        const allLayers = layersRef.current.map(l => l.toGeoJSON());
        if (onAreaUpdate) {
          if (allLayers.length === 1) {
            onAreaUpdate(JSON.stringify(allLayers[0].geometry));
          } else {
            const featureCollection = {
              type: "FeatureCollection",
              features: allLayers
            };
            onAreaUpdate(JSON.stringify(featureCollection));
          }
        }
      });

      // Obsługa usuwania kształtu
      layer.on('pm:remove', () => {
        const index = layersRef.current.indexOf(layer);
        if (index > -1) {
          layersRef.current.splice(index, 1);
        }
        
        // Aktualizuj parent
        const allLayers = layersRef.current.map(l => l.toGeoJSON());
        if (onAreaUpdate) {
          if (allLayers.length === 0) {
            onAreaUpdate(null);
          } else if (allLayers.length === 1) {
            onAreaUpdate(JSON.stringify(allLayers[0].geometry));
          } else {
            const featureCollection = {
              type: "FeatureCollection",
              features: allLayers
            };
            onAreaUpdate(JSON.stringify(featureCollection));
          }
        }
      });
    });

    return () => {
      map.pm.removeControls();
      map.off('pm:create');
    };
  }, [map, color, onAreaUpdate]);

  // Renderowanie istniejącego GeoJSON
  useEffect(() => {
    if (!map || !geoJsonData) return;

    layersRef.current.forEach(l => {
      if (l && l.remove) {
        l.remove();
      }
    });
    layersRef.current = [];

    const layer = L.geoJSON(geoJsonData, {
      style: {
        color: color || '#ff0000',
        weight: 3,
        fillColor: color || '#ff0000',
        fillOpacity: 0.3,
      },
      pmIgnore: false,
    });

    layer.addTo(map);

    layer.eachLayer((l) => {
      layersRef.current.push(l);

      if (l.pm) {
        l.pm.enable({
          allowSelfIntersection: false,
        });

        l.on('pm:edit', () => {
          const allLayers = layersRef.current.map(layer => layer.toGeoJSON());
          if (onAreaUpdate) {
            if (allLayers.length === 1) {
              onAreaUpdate(JSON.stringify(allLayers[0].geometry));
            } else {
              const featureCollection = {
                type: "FeatureCollection",
                features: allLayers
              };
              onAreaUpdate(JSON.stringify(featureCollection));
            }
          }
        });

        l.on('pm:remove', () => {
          const index = layersRef.current.indexOf(l);
          if (index > -1) {
            layersRef.current.splice(index, 1);
          }
          
          const allLayers = layersRef.current.map(layer => layer.toGeoJSON());
          if (onAreaUpdate) {
            if (allLayers.length === 0) {
              onAreaUpdate(null);
            } else if (allLayers.length === 1) {
              onAreaUpdate(JSON.stringify(allLayers[0].geometry));
            } else {
              const featureCollection = {
                type: "FeatureCollection",
                features: allLayers
              };
              onAreaUpdate(JSON.stringify(featureCollection));
            }
          }
        });
      }
    });

    if (layer.getBounds && layer.getBounds().isValid()) {
      map.fitBounds(layer.getBounds());
    }

    return () => {
      layersRef.current.forEach(l => {
        if (l && l.remove) {
          l.remove();
        }
      });
      layersRef.current = [];
    };
  }, [map, geoJsonData, color, onAreaUpdate]);

  return null;
};

/**
 * Props:
 * - pins?: Array<{ x: number, y: number, id: number }> (lista punktów)
 * - area?: string (GeoJSON w formacie string)
 * - color?: string (np. "#000000")
 * - zoom?: number
 * - style?: object
 * - editable?: boolean (czy mapa jest edytowalna)
 * - onAreaUpdate?: function (callback do aktualizacji obszaru)
 */
export default function InformationLayerMap({
  pins = [],
  area,
  color = "#0000ff",
  zoom = 15,
  style,
  editable = false,
  onAreaUpdate,
}) {
  const navigate = useNavigate();
  
const geoJsonData = useMemo(() => {
  if (!area) return null;

  try {
    const cleaned = DOMPurify.sanitize(area, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
    return JSON.parse(cleaned);
  } catch {
    console.error("Invalid GeoJSON");
    return null;
  }
}, [area]);


  const center = useMemo(() => {
    if (pins.length > 0) {
      const firstPin = pins[0];
      return [Number(firstPin.y), Number(firstPin.x)];
    }
    return [51.759, 19.458]; // fallback: Łódź
  }, [pins]);

  // Ikona dla pinów
  const smallIcon = useMemo(() => {
    return new L.Icon({
      iconUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-icon.png",
      iconRetinaUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-icon-2x.png",
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowUrl: null,
      shadowSize: [0, 0],
    });
  }, []);

  const FitBoundsToPins = ({ pins }) => {
    const map = useMap();
    useEffect(() => {
      if (pins.length > 0) {
        const bounds = pins.map((pin) => [Number(pin.y), Number(pin.x)]);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }, [pins, map]);
    return null;
  };

  const FitBounds = ({ geoJsonData }) => {
    const map = useMap();
    useEffect(() => {
      if (geoJsonData) {
        const layer = L.geoJSON(geoJsonData);
        map.fitBounds(layer.getBounds());
      }
    }, [geoJsonData, map]);
    return null;
  };

  return (
    <div
      className="map-container"
      style={{
        height: "850px",
        borderRadius: "24px",
        overflow: "hidden",
        boxShadow: "0 8px 20px rgba(0, 0, 0, 0.12)",
        ...style,
      }}
    >
      <MapComponent
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        closePopupOnClick={false}
      >
        {pins.length > 0 && <FitBoundsToPins pins={pins} />}

        {pins.map((pin, index) => {
          return (
            <Marker
              key={index}
              position={[Number(pin.y), Number(pin.x)]}
              icon={smallIcon}
              eventHandlers={{
                click: () => {
                  if (pin.id) {
                    navigate(`/information/${pin.id}`);
                  }
                }
              }}
            >
              <Popup>
                <div style={{ cursor: 'pointer' }} onClick={() => navigate(`/information/${pin.id}`)}>
                  Kliknij aby zobaczyć szczegóły
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Renderowanie obszaru z GeoJSON */}
        {editable ? (
          <EditableArea 
            geoJsonData={geoJsonData} 
            color={color} 
            onAreaUpdate={onAreaUpdate}
          />
        ) : (
          geoJsonData && (
            <>
              <GeoJSON
                key={JSON.stringify(geoJsonData)}
                data={geoJsonData}
                style={() => ({
                  color: color || "#ff0000",
                  weight: 3,
                  fillColor: color || "#ff0000",
                  fillOpacity: 0.3,
                })}
              />
              <FitBounds geoJsonData={geoJsonData} />
            </>
          )
        )}
        {!geoJsonData && console.log("No GeoJSON data to render")}
      </MapComponent>
    </div>
  );
}
